import React, { useState } from 'react';
import { Plus, Globe, RefreshCw, AlertTriangle, Edit, Trash2, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import useApiData from '@/hooks/useApiData';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';


export default function Hospedagem() {
  const { data: hostingsData, refresh: refreshHostings } = useApiData('/hostings');
  const { data: clientsData } = useApiData('/clients');
  const { data: contractsData } = useApiData('/contracts');

  const hostings = (hostingsData || []).filter(h => h.status !== 'inativa');
  const clients = clientsData || [];
  const contracts = contractsData || [];

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedHosting, setSelectedHosting] = useState(null);
  const [formData, setFormData] = useState({
    client_id: '',
    contract_id: '',
    domain: '',
    hosting_type: 'inclusa',
    provider: '',
    value: '',
    start_date: '',
    period: 'mensal',
    status: 'ativa',
    notes: '',
    auto_asaas_billing: false,
    asaas_billing_type: 'UNDEFINED'
  });
  const [loading, setLoading] = useState(false);

  const handleDelete = async (id) => {
    if (window.confirm("Deseja realmente inativar esta hospedagem?")) {
      try {
        await api.delete(`/hostings/${id}`);
        toast.success("Hospedagem inativada com sucesso");
        refreshHostings();
      } catch (error) {
        toast.error("Erro ao inativar hospedagem");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        value: parseFloat(formData.value),
        start_date: new Date(formData.start_date).toISOString(),
        contract_id: formData.contract_id || null
      };

      if (selectedHosting) {
        await api.put(`/hostings/${selectedHosting.id}`, payload);
        toast.success('Hospedagem atualizada com sucesso!');
      } else {
        await api.post('/hostings', payload);
        toast.success('Hospedagem criada com sucesso!');
      }
      setIsDialogOpen(false);
      resetForm();
      refreshHostings();

    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : `Erro ao ${selectedHosting ? 'atualizar' : 'criar'} hospedagem`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (hosting) => {
    setSelectedHosting(hosting);
    setFormData({
      client_id: hosting.client_id || '',
      contract_id: hosting.contract_id || '',
      domain: hosting.domain || '',
      hosting_type: hosting.hosting_type || 'inclusa',
      provider: hosting.provider || '',
      value: hosting.value !== undefined ? hosting.value : '',
      start_date: hosting.start_date ? new Date(hosting.start_date).toISOString().split('T')[0] : '',
      period: hosting.period || 'mensal',
      status: hosting.status || 'ativa',
      notes: hosting.notes || '',
      auto_asaas_billing: hosting.auto_asaas_billing || false,
      asaas_billing_type: hosting.asaas_billing_type || 'UNDEFINED'
    });
    setIsDialogOpen(true);
  };

  const handleRenew = async (hostingId) => {
    try {
      await api.post(`/hostings/${hostingId}/renew`);
      toast.success('Hospedagem renovada com sucesso!');
      refreshHostings();
    } catch (error) {
      toast.error('Erro ao renovar hospedagem');
    }
  };

  const resetForm = () => {
    setSelectedHosting(null);
    setFormData({
      client_id: '',
      contract_id: '',
      domain: '',
      hosting_type: 'inclusa',
      provider: '',
      value: '',
      start_date: '',
      period: 'mensal',
      status: 'ativa',
      notes: '',
      auto_asaas_billing: false,
      asaas_billing_type: 'UNDEFINED'
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      ativa: { variant: 'default', label: 'Ativa' },
      a_vencer: { variant: 'secondary', label: 'A Vencer' },
      vencida: { variant: 'destructive', label: 'Vencida' },
      cancelada: { variant: 'outline', label: 'Cancelada' }
    };
    const { variant, label } = config[status] || config.ativa;
    return <Badge variant={variant}>{label}</Badge>;
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'N/A';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6" data-testid="hospedagem-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight text-slate-900">Hospedagem</h1>
          <p className="text-slate-600 mt-1">Gerencie as hospedagens dos clientes</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          if (!open) resetForm();
          setIsDialogOpen(open);
        }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="add-hosting-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nova Hospedagem
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedHosting ? 'Editar Hospedagem' : 'Nova Hospedagem'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Cliente *</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                  required
                >
                  <SelectTrigger data-testid="select-hosting-client">
                    <SelectValue placeholder="Selecione um cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="domain">Domínio *</Label>
                <Input
                  id="domain"
                  placeholder="exemplo.com"
                  value={formData.domain}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  required
                  data-testid="hosting-domain-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select
                    value={formData.hosting_type}
                    onValueChange={(value) => setFormData({ ...formData, hosting_type: value })}
                  >
                    <SelectTrigger data-testid="hosting-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inclusa">Inclusa</SelectItem>
                      <SelectItem value="separada">Separada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="provider">Provedor</Label>
                  <Input
                    id="provider"
                    value={formData.provider}
                    onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                    data-testid="hosting-provider-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="value">Valor (R$) *</Label>
                  <Input
                    id="value"
                    type="number"
                    step="0.01"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    required
                    data-testid="hosting-value-input"
                  />
                </div>
                <div>
                  <Label>Período</Label>
                  <Select
                    value={formData.period}
                    onValueChange={(value) => setFormData({ ...formData, period: value })}
                  >
                    <SelectTrigger data-testid="hosting-period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mensal">Mensal</SelectItem>
                      <SelectItem value="anual">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="start_date">Data de Início *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                  data-testid="hosting-start-date"
                />
              </div>

              {selectedHosting && (
                <div>
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="hosting-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ativa">Ativa</SelectItem>
                      <SelectItem value="a_vencer">A Vencer</SelectItem>
                      <SelectItem value="vencida">Vencida</SelectItem>
                      <SelectItem value="cancelada">Cancelada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label htmlFor="notes">Observações</Label>
                <textarea
                  id="notes"
                  className="w-full min-h-[60px] px-3 py-2 text-sm rounded-md border border-input bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="hosting-notes"
                />
              </div>

              {/* === Automação Asaas === */}
              <div className="md:col-span-2 border border-violet-100 bg-violet-50/40 rounded-xl p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap size={16} className="text-violet-600" />
                    <span className="text-sm font-semibold text-slate-700">Cobranças Automáticas (Asaas)</span>
                  </div>
                  <Switch
                    id="auto_asaas_billing"
                    checked={formData.auto_asaas_billing}
                    onCheckedChange={(checked) => setFormData({ ...formData, auto_asaas_billing: checked })}
                  />
                </div>
                {formData.auto_asaas_billing && (
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-600">Forma de Pagamento Padrão</Label>
                    <Select
                      value={formData.asaas_billing_type}
                      onValueChange={(v) => setFormData({ ...formData, asaas_billing_type: v })}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Escolher modo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UNDEFINED">A Escolher / Deixar Cliente Escolher</SelectItem>
                        <SelectItem value="PIX">Apenas PIX</SelectItem>
                        <SelectItem value="BOLETO">Apenas Boleto</SelectItem>
                        <SelectItem value="CREDIT_CARD">Apenas Cartão de Crédito</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      As faturas serão criadas automaticamente no Asaas ao salvar ou renovar a hospedagem.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading} data-testid="submit-hosting-btn">
                  {loading ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Globe className="h-5 w-5 text-success" />
            <p className="text-sm font-medium text-slate-500">Hospedagens Ativas</p>
          </div>
          <p className="text-3xl font-bold text-slate-900">
            {hostings.filter(h => h.status === 'ativa').length}
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            <p className="text-sm font-medium text-slate-500">A Vencer (30 dias)</p>
          </div>
          <p className="text-3xl font-bold text-slate-900">
            {hostings.filter(h => h.status === 'a_vencer').length}
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="h-5 w-5 text-error" />
            <p className="text-sm font-medium text-slate-500">Vencidas</p>
          </div>
          <p className="text-3xl font-bold text-slate-900">
            {hostings.filter(h => h.status === 'vencida').length}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50/50 border-b border-slate-100">
              <tr>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Cliente</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Domínio</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Provedor</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Valor</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Vencimento</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Dias</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Status</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody>
              {hostings.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-slate-500">
                    Nenhuma hospedagem encontrada
                  </td>
                </tr>
              ) : (
                hostings.map((hosting) => (
                  <tr key={hosting.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`hosting-row-${hosting.id}`}>
                    <td className="p-4 align-middle text-sm font-medium text-slate-700">{getClientName(hosting.client_id)}</td>
                    <td className="p-4 align-middle text-sm text-slate-600">{hosting.domain}</td>
                    <td className="p-4 align-middle text-sm text-slate-600">{hosting.provider || 'N/A'}</td>
                    <td className="p-4 align-middle text-sm font-mono text-slate-900">R$ {hosting.value.toFixed(2)}</td>
                    <td className="p-4 align-middle text-sm text-slate-600">{formatDate(hosting.end_date)}</td>
                    <td className="p-4 align-middle text-sm">
                      <Badge variant={hosting.days_to_expire <= 7 ? 'destructive' : 'secondary'}>
                        {hosting.days_to_expire} dias
                      </Badge>
                    </td>
                    <td className="p-4 align-middle text-sm">{getStatusBadge(hosting.status)}</td>
                    <td className="p-4 align-middle text-sm">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRenew(hosting.id)}
                          data-testid={`renew-hosting-${hosting.id}`}
                          title="Renovar hospedagem"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(hosting)}
                          data-testid={`edit-hosting-${hosting.id}`}
                          title="Editar hospedagem"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(hosting.id)}
                          data-testid={`delete-hosting-${hosting.id}`}
                          title="Inativar hospedagem"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}